import { useState } from "react";
import { Link } from "react-router-dom";
import { get, post } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  RotateCcw, ArrowRight, ArrowLeft, IdCard, DoorOpen, ShieldCheck, Eye, EyeOff,
  CheckCircle2, Building2, BarChart3, Lock, FileCheck2, HelpCircle, Loader2, Check, XCircle,
} from "lucide-react";

const DEMO_WS = "ws_demo_alex";
const FIXED_REQUEST = {
  workspace_id: DEMO_WS, activity_name: "Drama Club", activity_type: "Drama / performance",
  room: "long_room", day: "tuesday", start: "09:00", end: "16:00",
  recurring: "Recurring", recur_range: "Weekly for the autumn term", attendance: 18,
  equipment: ["Open floor space", "Chairs", "Storage access"], safeguarding_involved: true,
};
const REQUEST = { name: "Alex Morgan", activity: "Drama Club", time: "Tuesdays, 09:00–16:00", size: 18, needs: "Open floor space, chairs, storage access" };

const HOME = "home", CARD = "card", RESPONSE = "response", PROTECTED = "protected", ROOM = "room", ADMIN = "admin", CEO = "ceo";
const STEP_OF = { [CARD]: 0, [RESPONSE]: 0, [PROTECTED]: 0, [ROOM]: 1, [ADMIN]: 2, [CEO]: 3 };
const STEPS = ["Volunteer", "Room Service", "Booking Administrator", "CEO"];
const SYNTH = "Illustrative sandbox scenario — no live personal data or live KATLAS node";

export default function SimpleDemo() {
  const [view, setView] = useState(HOME);
  const [offer, setOffer] = useState(null);
  const [booking, setBooking] = useState(null);
  const [choice, setChoice] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = async () => {
    setBusy(true);
    try { await post("/rooms/demo/reset", {}); } catch { /* non-blocking */ }
    setOffer(null); setBooking(null); setChoice(0); setConfirmed(false); setView(HOME); setBusy(false);
    toast.success("Demo reset.");
  };

  const checkAvailability = async () => {
    setBusy(true);
    try { const o = await post("/rooms/availability", FIXED_REQUEST); setOffer(o); setView(RESPONSE); }
    catch { toast.error("Could not check availability."); }
    setBusy(false);
  };

  const bookChoice = async (idx) => {
    if (!offer) return;
    setBusy(true);
    try {
      const c = offer.primary && idx === -1 ? "primary" : `alt:${idx}`;
      const b = await post(`/rooms/offers/${offer.id}/book`, { choice: c });
      setBooking(b); setChoice(idx); setView(PROTECTED);
    } catch { toast.error("Could not book that option."); }
    setBusy(false);
  };

  // For direct entry via the home secondary buttons — make sure real data exists.
  const ensureAndGo = async (target) => {
    setBusy(true);
    try {
      let o = offer; if (!o) { o = await post("/rooms/availability", FIXED_REQUEST); setOffer(o); }
      let b = booking; if (!b) { b = await post(`/rooms/offers/${o.id}/book`, { choice: o.primary ? "primary" : "alt:0" }); setBooking(b); }
      setView(target);
    } catch { toast.error("Could not prepare the demo."); }
    setBusy(false);
  };

  const confirmBooking = async () => {
    if (!booking) return;
    setBusy(true);
    try { const b = await post(`/rooms/bookings/${booking.id}/decision`, { action: "confirm", message: "Confirmed for the term." }); setBooking(b); setConfirmed(true); toast.success("Booking confirmed."); }
    catch { toast.error("Could not confirm."); }
    setBusy(false);
  };

  return (
    <div className="min-h-[calc(100vh-56px)] bg-mill-bg" data-testid="simple-demo">
      <div className="mx-auto max-w-2xl px-5 pb-20 pt-4 sm:px-8">
        <div className="mb-5 flex items-center justify-between gap-3">
          <ProgressStrip current={view === HOME ? -1 : STEP_OF[view]} />
          <Button size="sm" variant="ghost" onClick={reset} disabled={busy} data-testid="reset-replay" className="shrink-0 text-xs text-mill-muted hover:text-mill-ink"><RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset</Button>
        </div>
        <p className="mb-4 text-[11px] text-mill-muted" data-testid="synthetic-label">{SYNTH}</p>

        {view === HOME && <Home go={setView} ensureAndGo={ensureAndGo} busy={busy} />}
        {view === CARD && <CardScreen go={setView} onCheck={checkAvailability} busy={busy} />}
        {view === RESPONSE && <ResponseScreen go={setView} offer={offer} choice={choice} setChoice={setChoice} onBook={bookChoice} busy={busy} />}
        {view === PROTECTED && <ProtectedScreen go={setView} booking={booking} />}
        {view === ROOM && <RoomServiceScreen go={setView} offer={offer} />}
        {view === ADMIN && <AdminScreen go={setView} booking={booking} confirmed={confirmed} onConfirm={confirmBooking} onOffer={() => setView(RESPONSE)} busy={busy} />}
        {view === CEO && <CeoScreen go={setView} />}
      </div>
    </div>
  );
}

function ProgressStrip({ current }) {
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px]" data-testid="progress-strip">
      {STEPS.map((s, i) => (
        <span key={s} className="flex items-center gap-1.5">
          <span data-testid={`step-${i}${current === i ? "-active" : ""}`}
            className={`rounded-full px-2 py-0.5 font-medium ${current === i ? "bg-mill-moss text-white" : "text-mill-muted"}`}>{i + 1} {s}</span>
          {i < STEPS.length - 1 && <span className="text-stone-300">→</span>}
        </span>
      ))}
    </div>
  );
}

const Card = ({ children, testid }) => <div data-testid={testid} className="rounded-3xl border border-stone-200 bg-white p-7 sm:p-9">{children}</div>;
const Big = ({ children, onClick, testid, disabled, className = "" }) => (
  <Button onClick={onClick} disabled={disabled} data-testid={testid} className={`h-14 w-full justify-between rounded-2xl px-6 text-base font-semibold ${className}`}>{children}</Button>
);
const Back = ({ onClick, label = "Back" }) => (
  <button onClick={onClick} className="mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-mill-muted hover:text-mill-ink" data-testid="demo-back"><ArrowLeft className="h-4 w-4" /> {label}</button>
);
const Field = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-stone-100 py-2.5"><span className="text-sm text-mill-muted">{label}</span><span className="text-right text-sm font-medium text-mill-ink">{value}</span></div>
);
const List = ({ items, tone }) => (
  <ul className="mt-2 space-y-1.5">{items.map((t) => (<li key={t} className="flex items-start gap-2 text-sm text-mill-ink">{tone === "yes" ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#065F46]" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#991B1B]" />} {t}</li>))}</ul>
);
const SecondaryBtn = ({ onClick, icon: Icon, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className="flex w-full items-center gap-3 rounded-2xl border border-stone-200 bg-white px-5 py-3.5 text-left text-sm font-medium text-mill-ink transition-colors hover:border-mill-moss/40 hover:bg-mill-alt/40"><Icon className="h-4 w-4 text-mill-moss" /> {children}</button>
);

function Home({ go, ensureAndGo, busy }) {
  return (
    <div data-testid="demo-home">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-mill-moss">The Mill · Simple demonstration</p>
      <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-mill-ink sm:text-5xl">I am a volunteer</h1>
      <p className="mt-3 text-lg text-mill-muted">I want to run an activity at The Mill.</p>
      <div className="mt-8"><Big onClick={() => go(CARD)} testid="start-request" className="bg-mill-moss text-white hover:bg-mill-moss-dark">Start my room request <ArrowRight className="h-5 w-5" /></Big></div>
      <p className="mt-10 text-xs font-semibold uppercase tracking-wider text-mill-muted">Or see the other perspectives</p>
      <div className="mt-3 space-y-2.5">
        <SecondaryBtn onClick={() => ensureAndGo(ROOM)} icon={Building2} testid="see-room-service">See what the Room Service sees</SecondaryBtn>
        <SecondaryBtn onClick={() => ensureAndGo(ADMIN)} icon={ShieldCheck} testid="see-admin">See what the Booking Administrator sees</SecondaryBtn>
        <SecondaryBtn onClick={() => go(CEO)} icon={BarChart3} testid="see-ceo">See the CEO activity and room-use report</SecondaryBtn>
      </div>
      {busy && <p className="mt-4 flex items-center gap-1.5 text-xs text-mill-muted"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Preparing…</p>}
      <p className="mt-10 text-center text-xs text-mill-muted"><Link to="/hub" className="hover:text-mill-ink" data-testid="link-operational-tool">Full operational tool &amp; reference →</Link></p>
    </div>
  );
}

function CardScreen({ go, onCheck, busy }) {
  return (
    <div>
      <Back onClick={() => go(HOME)} label="Home" />
      <Card testid="demo-card">
        <div className="flex items-center gap-2 text-mill-moss"><IdCard className="h-5 w-5" /><p className="text-sm font-semibold uppercase tracking-wide">Act 1 · Volunteer</p></div>
        <h1 className="mt-2 font-display text-3xl font-bold text-mill-ink">Create your Mill Card</h1>
        <p className="mt-2 text-mill-muted">A private space for your Mill role, requests and choices about what you share.</p>
        <div className="mt-5">
          <Field label="Name" value={REQUEST.name} />
          <Field label="I want to run" value={REQUEST.activity} />
          <Field label="Preferred time" value={REQUEST.time} />
          <Field label="Expected group size" value={`${REQUEST.size} people`} />
          <Field label="Needs" value={REQUEST.needs} />
        </div>
        <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#D1FAE5] px-3.5 py-1.5 text-sm font-semibold text-[#065F46]" data-testid="volunteer-active"><CheckCircle2 className="h-4 w-4" /> Volunteer authority active — demo</div>
        <p className="mt-3 text-xs text-mill-muted">We don't ask for equality, health, ethnicity, sexuality, postcode or identity details here. Any such information stays private and is only ever used as anonymous aggregate data later.</p>
        <div className="mt-6"><Big onClick={onCheck} disabled={busy} testid="check-availability" className="bg-mill-moss text-white hover:bg-mill-moss-dark">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Check room availability <ArrowRight className="h-5 w-5" /></>}</Big></div>
      </Card>
    </div>
  );
}

function ResponseScreen({ go, offer, choice, setChoice, onBook, busy }) {
  const alts = offer?.alternatives || [];
  return (
    <div>
      <Back onClick={() => go(CARD)} label="Change my request" />
      <Card testid="demo-response">
        <div className="flex items-center gap-2 text-mill-moss"><DoorOpen className="h-5 w-5" /><p className="text-sm font-semibold uppercase tracking-wide">Your room request</p></div>
        <div className="mt-3 rounded-2xl bg-mill-alt/50 p-4 text-sm">
          <p className="font-semibold text-mill-ink">{REQUEST.activity} · {REQUEST.time}</p>
          <p className="text-mill-muted">{REQUEST.size} people · {REQUEST.needs}</p>
        </div>
        <p className="mt-4 flex items-start gap-2 rounded-xl bg-[#FFFBEB] px-4 py-3 text-sm text-[#B45309]" data-testid="response-message"><Lock className="mt-0.5 h-4 w-4 shrink-0" /> The room you asked for is not available for the full time you requested. Here are options that work.</p>

        <div className="mt-4 space-y-2.5">
          {offer?.primary && (
            <button onClick={() => setChoice(-1)} data-testid="alt-primary" className={`flex w-full items-center justify-between gap-3 rounded-2xl border-2 p-4 text-left ${choice === -1 ? "border-mill-moss bg-mill-alt/40" : "border-stone-200 hover:border-mill-moss/40"}`}>
              <span><span className="block font-semibold capitalize text-mill-ink">{offer.primary.room} · {offer.primary.day} · {offer.primary.start}–{offer.primary.end}</span><span className="block text-sm text-mill-muted">Your requested room and time</span></span>
              {choice === -1 ? <span className="flex h-6 w-6 items-center justify-center rounded-full bg-mill-moss text-white"><Check className="h-4 w-4" /></span> : <span className="h-6 w-6 rounded-full border-2 border-stone-300" />}
            </button>
          )}
          {alts.map((a, i) => (
            <button key={i} onClick={() => setChoice(i)} data-testid={`alt-${i}`} className={`flex w-full items-center justify-between gap-3 rounded-2xl border-2 p-4 text-left ${choice === i ? "border-mill-moss bg-mill-alt/40" : "border-stone-200 hover:border-mill-moss/40"}`}>
              <span><span className="block font-semibold capitalize text-mill-ink">{a.room} · {a.day} · {a.start}–{a.end}</span><span className="block text-sm text-mill-muted">{a.note}</span></span>
              {choice === i ? <span className="flex h-6 w-6 items-center justify-center rounded-full bg-mill-moss text-white"><Check className="h-4 w-4" /></span> : <span className="h-6 w-6 rounded-full border-2 border-stone-300" />}
            </button>
          ))}
          {alts.length === 0 && !offer?.primary && <p className="text-sm text-mill-muted">No automatic options found — you can ask the Booking Team for help.</p>}
        </div>

        <div className="mt-5 flex flex-col gap-2.5">
          <Big onClick={() => onBook(choice)} disabled={busy} testid="choose-option" className="bg-mill-moss text-white hover:bg-mill-moss-dark">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Choose this option <ArrowRight className="h-5 w-5" /></>}</Big>
          <button onClick={() => onBook(choice)} className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-mill-muted hover:text-mill-ink" data-testid="ask-booking-team"><HelpCircle className="h-4 w-4" /> Ask Booking Team for help</button>
        </div>
        <p className="mt-3 text-xs text-mill-muted">You're shown only availability and bounded alternatives — never another group's booking, name, activity or the reason a room is busy.</p>
      </Card>
    </div>
  );
}

function ProtectedScreen({ go, booking }) {
  return (
    <div>
      <Back onClick={() => go(RESPONSE)} label="Back to options" />
      <Card testid="demo-protected">
        <h1 className="font-display text-2xl font-bold text-mill-ink">What just happened</h1>
        {booking && <p className="mt-1 text-sm text-mill-muted">You chose <b className="capitalize text-mill-ink">{booking.room}, {booking.day} {booking.start}–{booking.end}</b>.</p>}
        <div className="mt-5 rounded-2xl border border-[#A7F3D0] bg-[#F0FDF4] p-4" data-testid="shared-panel">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-[#065F46]"><Eye className="h-4 w-4" /> Shared with the Room Service</p>
          <List tone="yes" items={["I am an active authorised volunteer", "Drama activity", "Requested day and time", "Expected attendance", "Room and access needs"]} />
        </div>
        <div className="mt-3 rounded-2xl border border-[#FECACA] bg-[#FEF2F2] p-4" data-testid="not-shared-panel">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-[#991B1B]"><EyeOff className="h-4 w-4" /> Not shared</p>
          <List tone="no" items={["My private profile information", "Optional background or equality information", "My other Mill activity", "Who else has booked rooms", "Why another room booking exists"]} />
        </div>
        <div className="mt-3 rounded-2xl bg-mill-alt/50 p-4" data-testid="evidence-panel">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-mill-ink"><FileCheck2 className="h-4 w-4 text-[#B45309]" /> Evidence created</p>
          <ul className="mt-2 space-y-1 text-sm text-mill-muted"><li>Room request submitted</li><li>Availability checked</li><li>Alternative offered</li><li>Booking decision awaiting confirmation</li></ul>
          {booking?.evidence?.[0] && <p className="mt-2 text-[11px] text-mill-muted">{booking.evidence[0].text} · {booking.evidence[0].verification}</p>}
        </div>
        <div className="mt-6"><Big onClick={() => go(ROOM)} testid="see-other-side" className="bg-mill-ink text-white hover:bg-mill-ink/90">See the other side <ArrowRight className="h-5 w-5" /></Big></div>
      </Card>
    </div>
  );
}

function RoomServiceScreen({ go, offer }) {
  const outcome = offer?.result_type === "available" ? "Available" : offer?.result_type === "partial" ? "Partial availability" : "Not available for the full slot — alternatives offered";
  return (
    <div>
      <Back onClick={() => go(HOME)} label="Home" />
      <Card testid="demo-room-service">
        <div className="flex items-center gap-2 text-mill-moss"><Building2 className="h-5 w-5" /><p className="text-sm font-semibold uppercase tracking-wide">Act 2 · Room Service</p></div>
        <h1 className="mt-2 font-display text-2xl font-bold text-mill-ink">Room Service view</h1>
        <p className="mt-3 text-sm font-semibold text-mill-ink">What the room can check</p>
        <div className="mt-2">
          <Field label="Authorised requester" value="Yes" />
          <Field label="Activity type" value="Drama Club" />
          <Field label="Requested slot" value="Tuesday, 09:00–16:00" />
          <Field label="People expected" value="18" />
          <Field label="Needs" value="Open floor, chairs, storage" />
          <Field label="Private room calendar" value="Checked" />
          <Field label="Outcome" value={outcome} />
        </div>
        <div className="mt-4 rounded-2xl border border-[#FECACA] bg-[#FEF2F2] p-4" data-testid="room-cannot-see">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-[#991B1B]"><EyeOff className="h-4 w-4" /> What the Room Service cannot see</p>
          <List tone="no" items={["The volunteer's sensitive or optional profile information", "Equality or health-related responses", "Other rooms' booking names", "Other room users", "Other activity details", "Private staff notes"]} />
        </div>
        <p className="mt-3 text-sm text-mill-muted">The Room Service can evaluate the request without revealing the private schedule or the volunteer's wider profile.</p>
        <div className="mt-6"><Big onClick={() => go(ADMIN)} testid="open-admin" className="bg-mill-ink text-white hover:bg-mill-ink/90">Open Booking Administrator view <ArrowRight className="h-5 w-5" /></Big></div>
      </Card>
    </div>
  );
}

function AdminScreen({ go, booking, confirmed, onConfirm, onOffer, busy }) {
  return (
    <div>
      <Back onClick={() => go(HOME)} label="Home" />
      <Card testid="demo-admin">
        <div className="flex items-center gap-2 text-mill-moss"><ShieldCheck className="h-5 w-5" /><p className="text-sm font-semibold uppercase tracking-wide">Act 3 · Booking Administrator</p></div>
        <h1 className="mt-2 font-display text-2xl font-bold text-mill-ink">Booking Administrator view</h1>
        <div className="mt-4 rounded-2xl border border-stone-200 p-4" data-testid="admin-request-card">
          <p className="font-semibold text-mill-ink">Alex Morgan — Drama Club</p>
          <p className="text-sm text-mill-muted">Requested: Tuesdays, 09:00–16:00 · 18 people</p>
          <p className="mt-1 text-sm text-mill-muted">Request status: {confirmed ? "confirmed" : "alternative selected"}</p>
          {booking && <p className="mt-1 text-sm font-medium capitalize text-mill-ink">Proposed booking: {booking.room}, {booking.day} {booking.start}–{booking.end}</p>}
        </div>
        {confirmed ? (
          <div className="mt-4 rounded-2xl border border-[#A7F3D0] bg-[#F0FDF4] p-5 text-center" data-testid="booking-confirmed">
            <CheckCircle2 className="mx-auto h-8 w-8 text-[#065F46]" />
            <p className="mt-2 font-display text-lg font-bold text-mill-ink">Booking confirmed</p>
            <p className="mt-1 text-sm text-mill-muted">Both the volunteer and Booking Team have a record of the decision.</p>
            {booking?.decision?.evidence && <p className="mt-2 text-[11px] text-mill-muted">{booking.decision.evidence.text} · {booking.decision.evidence.verification}</p>}
            <Button className="mt-4 gap-2 bg-mill-ink text-white hover:bg-mill-ink/90" onClick={() => go(CEO)} data-testid="to-ceo">See the CEO report <ArrowRight className="h-4 w-4" /></Button>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-2.5">
            <Button className="h-12 bg-mill-moss text-white hover:bg-mill-moss-dark" onClick={onConfirm} disabled={busy} data-testid="confirm-booking">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm booking"}</Button>
            <Button variant="outline" className="h-12" onClick={onOffer} data-testid="offer-another">Offer another option</Button>
            <Button variant="outline" className="h-12" onClick={() => toast("Question sent to the volunteer (demo).")} data-testid="ask-question">Ask a question</Button>
            <Button variant="outline" className="h-12 text-[#991B1B]" onClick={() => toast("Request declined (demo).")} data-testid="decline">Decline</Button>
          </div>
        )}
        <div className="mt-5 rounded-2xl border border-[#A7F3D0] bg-[#F0FDF4] p-4" data-testid="admin-can-see">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-[#065F46]"><Eye className="h-4 w-4" /> What the Booking Administrator can see</p>
          <List tone="yes" items={["Volunteer name and agreed contact route", "Active volunteer status", "Request details", "Room alternatives", "Operational requirements", "Request and decision history"]} />
        </div>
        <div className="mt-3 rounded-2xl border border-[#FECACA] bg-[#FEF2F2] p-4" data-testid="admin-cannot-see">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-[#991B1B]"><EyeOff className="h-4 w-4" /> What the Booking Administrator cannot see</p>
          <List tone="no" items={["Private equality or health information", "Optional demographic information", "Individual activity-evaluation answers", "Other people's private profiles", "Restricted safeguarding or incident records"]} />
        </div>
      </Card>
    </div>
  );
}

function CeoScreen({ go }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { setBusy(true); try { setR(await get("/reports/ceo")); } catch { toast.error("Could not create report."); } setBusy(false); };
  const Row = ({ label, value }) => <div className="flex justify-between border-b border-stone-100 py-1.5 text-sm"><span className="text-mill-muted">{label}</span><span className="font-semibold text-mill-ink">{value}</span></div>;
  const Buckets = ({ label, data }) => data ? (
    <div className="mt-3"><p className="text-xs font-semibold uppercase tracking-wider text-mill-muted">{label}</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {Object.entries(data.shown).map(([k, v]) => <span key={k} className="rounded-full bg-mill-alt px-2.5 py-1 text-xs text-mill-ink">{k}: {v}</span>)}
        {data.suppressed.map((s) => <span key={s} className="rounded-full bg-stone-100 px-2.5 py-1 text-xs text-stone-500">{s}: suppressed</span>)}
      </div></div>) : null;
  return (
    <div>
      <Back onClick={() => go(HOME)} label="Home" />
      <Card testid="demo-ceo">
        <div className="flex items-center gap-2 text-mill-moss"><BarChart3 className="h-5 w-5" /><p className="text-sm font-semibold uppercase tracking-wide">Act 4 · CEO view</p></div>
        <h1 className="mt-2 font-display text-2xl font-bold text-mill-ink">Room use and activity evidence</h1>
        {!r && <div className="mt-6"><Big onClick={load} disabled={busy} testid="create-ceo-report" className="bg-mill-moss text-white hover:bg-mill-moss-dark">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Create room use and activity report <ArrowRight className="h-5 w-5" /></>}</Big></div>}
        {r && (
          <div className="mt-5 space-y-4" data-testid="ceo-report">
            <div>
              <Row label="Room hours requested" value={r.room_hours_requested} />
              <Row label="Room hours confirmed" value={r.room_hours_confirmed} />
              <Row label="First-time participation" value={`${r.first_time_pct}%`} />
              <Row label="Average enjoyment (1–5)" value={r.avg_enjoyment} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-mill-muted">Room utilisation</p>
              <div className="mt-2 space-y-1.5">
                {r.room_use.map((u) => (
                  <div key={u.room} className="flex items-center gap-3 text-sm">
                    <span className="w-32 shrink-0 text-mill-ink">{u.room}</span>
                    <span className="h-2 flex-1 overflow-hidden rounded-full bg-stone-100"><span className="block h-full rounded-full bg-mill-moss" style={{ width: `${u.utilisation_pct}%` }} /></span>
                    <span className="w-10 text-right text-mill-muted">{u.utilisation_pct}%</span>
                  </div>
                ))}
              </div>
            </div>
            <Buckets label="Most requested activity types" data={r.activity_demand} />
            <Buckets label="Broad reach (postcode area)" data={r.reach} />
            <div><p className="text-xs font-semibold uppercase tracking-wider text-mill-muted">Busiest patterns</p><p className="mt-1 text-sm text-mill-ink">{r.busiest_patterns.join(" · ")}</p></div>
            <div className="rounded-2xl border border-dashed border-stone-300 p-4" data-testid="ceo-manifest">
              <p className="text-sm font-semibold text-mill-ink">How this report was created</p>
              <dl className="mt-2 space-y-1 text-xs">
                <div><dt className="text-mill-muted">Purpose</dt><dd className="text-mill-ink">{r.manifest.purpose}</dd></div>
                <div><dt className="text-mill-muted">Data source</dt><dd className="text-mill-ink">{r.manifest.data_source}</dd></div>
                <div><dt className="text-mill-muted">Privacy rule</dt><dd className="text-mill-ink">{r.manifest.privacy_rule}</dd></div>
                <div><dt className="text-mill-muted">Status</dt><dd className="text-mill-ink">{r.manifest.status}</dd></div>
              </dl>
              <p className="mt-2 text-[11px] text-mill-muted">No participant list, no raw questionnaire responses, and no protected-characteristic detail below the agreed cohort threshold.</p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
